// src/config/serviceImages.js
//
// Standard image registry — every real photo used across the app (service
// task thumbnails, plan hero images, etc.) is imported ONCE here from
// src/components/images/stage_01/ and re-exported by name. Pages import
// from this file instead of reaching into the images folder directly, so
// the same "Mopping" or "Bathroom Cleaning" photo is reused everywhere
// that task is shown, rather than every page picking its own asset.
//
// To add a new photo: drop the file into components/images/stage_01/,
// import it below, and add it to SERVICE_IMAGES (or PLAN_HERO_IMAGES).

import moppingImg          from "../components/images/stage_01/mopping_cleaning.png";
import glassCleaningImg    from "../components/images/stage_01/glass_cleaning.png";
import utensilsCleaningImg from "../components/images/stage_01/utils_cleaning.png";
import stoveCleaningImg    from "../components/images/stage_01/stove_cleaning.png";
import kitchenCleaningImg  from "../components/images/stage_01/kitchen_utensil_cleaning.png";
import bathroomCleaningImg from "../components/images/stage_01/bathroom_cleaning.png";
import bedroomCleaningImg  from "../components/images/stage_01/bedroom_cleaning.png";
import balconyCleaningImg  from "../components/images/stage_01/balcony_cleaning.png";
import wardrobeCleaningImg from "../components/images/stage_01/wordrobe_cleaning.png";
import furnitureCleaningImg from "../components/images/stage_01/furniture & display cabients cleaning.png";
import slabCleaningImg     from "../components/images/stage_01/slab_cleaning.png";

import rapidHomeCleaningImg from "../components/images/stage_01/rapid_home_cleaning_R2.png";
import deepCleaningImg      from "../components/images/stage_01/deep_cleaning.png";
import completeCleaningImg  from "../components/images/stage_01/complete_house_cleaning.png";
import livingroomBalconyImg from "../components/images/stage_01/livingroom_balcony.png";

import fanCleaningImg from "../components/images/stage_01/window,fan& switch board cleaning.png";
import electricalImg  from "../components/images/stage_01/electrical.png";
import gardeningImg   from "../components/images/stage_01/gardening.png";

// Bronze Package's 17-service scope — the categories not already covered
// by a task photo above.
import cookingPrepImg    from "../components/images/stage_01/cooking preparation.png";
import dustingWipingImg  from "../components/images/stage_01/sweeping,dusting,mopping,wiping.png";
import cabinetCleaningImg from "../components/images/stage_01/cabinet_cleaning.png";
import packingUnpackingImg from "../components/images/stage_01/packing_unpacking.png";
import fridgeCleaningImg from "../components/images/stage_01/refrigetor_cleaning.png";
import microwaveCleaningImg from "../components/images/stage_01/refrigarator,chimney,microwave cleaning.png";

// Gold Package's extra 8 categories beyond Bronze's 17.
import chimneyCleaningImg  from "../components/images/stage_01/chimney_cleaning.png";
import grillParkingImg     from "../components/images/stage_01/gate&grill_Cleaning.jpeg";
// PLACEHOLDER — no dedicated plain photo exists yet for these 3; reusing
// the closest QAS_* branded card (title baked into the artwork itself, so
// it duplicates the text label shown alongside it here) until a real photo
// is supplied. Swap these out once one exists.
import rangoliImg   from "../components/images/stage_01/QAS_IHRA.png";
import fishTankImg  from "../components/images/stage_01/QAS_IHFT.png";
import bedArrangeImg from "../components/images/stage_01/QAS_IHBA.png";

// TEMP: plumber_service.png was archived out of stage_01 (moved to OLD/) with
// no replacement added yet — using the site's generic fallback photo until a
// new plumbing image is dropped into stage_01.
const plumbingImg = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";

// ── Per-task service photos — keyed by the exact task name used across the
// app (membership "included services", service page sub-service lists, …).
export const SERVICE_IMAGES = {
  "Mopping":            moppingImg,
  "Window Cleaning":    glassCleaningImg,
  "Utensils Cleaning":  utensilsCleaningImg,
  "Stove Cleaning":     stoveCleaningImg,
  "Kitchen Cleaning":   kitchenCleaningImg,
  "Bathroom Cleaning":  bathroomCleaningImg,
  "Bedroom Cleaning":   bedroomCleaningImg,
  "Balcony Cleaning":   balconyCleaningImg,
  "Wardrobe Cleaning":  wardrobeCleaningImg,
  "Furniture Cleaning": furnitureCleaningImg,
  "Slab Cleaning":      slabCleaningImg,
  "Sofa Cleaning":            furnitureCleaningImg,
  "Full Home Deep Cleaning":  deepCleaningImg,
  "Complete House Cleaning":  completeCleaningImg,
  "Living Room Cleaning":     livingroomBalconyImg,
  "Fan Cleaning":             fanCleaningImg,
  "Electrical":               electricalImg,
  "Plumbing":                 plumbingImg,
  "Gardening":                gardeningImg,

  // Aliases — membership plan copy (e.g. weekly-care's included/excluded
  // lists) uses slightly different wording than the task names above;
  // these map that wording back to the same photos instead of falling
  // through to the generic default image.
  "Utensil Cleaning":              utensilsCleaningImg,
  "Sweeping & Mopping":            moppingImg,
  "Fan & Ceiling":                 fanCleaningImg,
  "Window & Glass / Wall & Door":  glassCleaningImg,
  "Balcony & Entrance Cleaning":   balconyCleaningImg,
  "Deep Cleaning":                 deepCleaningImg,

  // Bronze Package's remaining included-services — not covered by any
  // task name above.
  "Food Preparation":                      cookingPrepImg,
  "Dusting & Wiping":                       dustingWipingImg,
  "Wardrobe Arrangement":                   wardrobeCleaningImg,
  "Kitchen Cabinet Cleaning":                cabinetCleaningImg,
  "Packing & Unpacking":                     packingUnpackingImg,
  "Refrigerator Cleaning":                   fridgeCleaningImg,
  "Microwave / Oven / Grill Cleaning":       microwaveCleaningImg,
  "Display Cabinet / Showcase Cleaning":     furnitureCleaningImg,

  // Gold Package's extra 8 categories beyond Bronze's 17 ("Bedroom
  // Cleaning" reuses the existing bedroomCleaningImg mapping above —
  // Study/Working Table and Dressing Table have no dedicated or QAS photo
  // yet, so they fall through to the generic DEFAULT_SERVICE_IMAGE below).
  "Sand Paint / Floor Art / Rangoli":        rangoliImg,
  "Chimney Cleaning":                        chimneyCleaningImg,
  "Steps & Parking Space Cleaning": grillParkingImg,
  "Fish Tank Cleaning":                      fishTankImg,
  "Bed Arrangement":                         bedArrangeImg,
};

const DEFAULT_SERVICE_IMAGE = moppingImg;

export function getServiceImage(name, fallback = DEFAULT_SERVICE_IMAGE) {
  return SERVICE_IMAGES[name] || fallback;
}

// ── Membership plan hero images — one per plan id, reusing the same local
// asset set (no external/stock photos).
export const PLAN_HERO_IMAGES = {
  "weekly-care":      [rapidHomeCleaningImg, moppingImg],
  "monthly-care":     [bedroomCleaningImg, completeCleaningImg],
  "premium-family":   [deepCleaningImg, livingroomBalconyImg],
  "silver-package":   [completeCleaningImg, bedroomCleaningImg],
  "silver-plus-package": [completeCleaningImg, bedroomCleaningImg],
  "golden-package":   [livingroomBalconyImg, deepCleaningImg],
  "platinum-package": [balconyCleaningImg, livingroomBalconyImg],
  "platinum-plus-package": [balconyCleaningImg, livingroomBalconyImg],
};

export function getPlanHeroImages(planId) {
  return PLAN_HERO_IMAGES[planId] || [rapidHomeCleaningImg];
}

// Generic local fallback for any hero/slider image that fails to load
// (replaces the old external Unsplash fallback URL).
export const DEFAULT_HERO_IMAGE = rapidHomeCleaningImg;

// ── Schedule Booking category cards — one photo per category key ──────────
export const CATEGORY_HERO_IMAGES = {
  home_cleaning: completeCleaningImg,
  technical:     electricalImg,
  outdoor:       gardeningImg,
};

export function getCategoryHeroImage(categoryKey) {
  return CATEGORY_HERO_IMAGES[categoryKey] || DEFAULT_HERO_IMAGE;
}
